import React, { useEffect, useState } from "react";
import { Card, Typography, Tooltip, Progress } from "antd";
import {
  UserAddOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  FileTextOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import useLeadStore from "../store/leadStore";
import useDealStore from "../store/dealStore";

const { Title, Text } = Typography;

const LeadConversionFunnel = () => {
  const { stats: leadStats, fetchLeadStats } = useLeadStore();
  const { stats: dealStats, fetchDealStats } = useDealStore();
  const [funnelData, setFunnelData] = useState([]);

  useEffect(() => {
    fetchLeadStats();
    fetchDealStats();
  }, [fetchLeadStats, fetchDealStats]);

  useEffect(() => {
    if (leadStats && dealStats) {
      const totalLeads = leadStats.totalLeads || 0;
      
      // Get actual lead status counts
      const newLeads =
        leadStats.statusBreakdown?.find((s) => s._id === "new")?.count || 0;
      const contacted =
        leadStats.statusBreakdown?.find((s) => s._id === "contacted")?.count || 0;
      const qualified =
        leadStats.statusBreakdown?.find((s) => s._id === "qualified")?.count || 0;
      const proposal =
        leadStats.statusBreakdown?.find((s) => s._id === "proposal")?.count || 0;
      const negotiation =
        leadStats.statusBreakdown?.find((s) => s._id === "negotiation")?.count || 0;
      
      // Get converted leads (leads that became deals)
      const converted =
        leadStats.statusBreakdown?.find((s) => s._id === "converted")?.count || 0;
      
      // Get closed won deals
      const closedWon =
        dealStats.stageBreakdown?.find((s) => s._id === "closed_won")?.count || 0;

      const stages = [
        {
          name: "New Leads",
          count: newLeads,
          icon: <UserAddOutlined />,
          color: "#1890ff",
          percentage: 100,
        },
        {
          name: "Contacted",
          count: contacted,
          icon: <PhoneOutlined className="icon-flip-horizontal" />,
          color: "#52c41a",
          percentage: newLeads > 0 ? (contacted / newLeads) * 100 : 0,
        },
        {
          name: "Qualified",
          count: qualified,
          icon: <MailOutlined />,
          color: "#faad14",
          percentage: newLeads > 0 ? (qualified / newLeads) * 100 : 0,
        },
        {
          name: "Proposal",
          count: proposal,
          icon: <FileTextOutlined />,
          color: "#722ed1",
          percentage: newLeads > 0 ? (proposal / newLeads) * 100 : 0,
        },
        {
          name: "Negotiation",
          count: negotiation,
          icon: <CalendarOutlined />,
          color: "#fa541c",
          percentage: newLeads > 0 ? (negotiation / newLeads) * 100 : 0,
        },
        {
          name: "Converted to Deal",
          count: converted,
          icon: <TrophyOutlined />,
          color: "#13c2c2",
          percentage: newLeads > 0 ? (converted / newLeads) * 100 : 0,
        },
        {
          name: "Closed Won",
          count: closedWon,
          icon: <TrophyOutlined />,
          color: "#52c41a",
          percentage: newLeads > 0 ? (closedWon / newLeads) * 100 : 0,
        },
      ];

      setFunnelData(stages);
    }
  }, [leadStats, dealStats]);

  const getStageWidth = (index, total) => {
    // Create a proper funnel shape - each stage gets progressively smaller
    const baseWidth = 100;
    const reduction = index * 12; // Reduce by 12% for each stage
    return Math.max(baseWidth - reduction, 30); // Minimum 30% width
  };

  return (
    <Card className="h-full shadow-soft bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900">
      <div className="text-center mb-8">
        <Title
          level={3}
          className="mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
        >
          Lead Conversion Funnel
        </Title>
        <Text className="text-gray-600 dark:text-gray-400">
          Track your leads through the entire conversion process
        </Text>
      </div>

      <div className="relative">
        {/* Funnel Container */}
        <div className="space-y-1">
          {funnelData.map((stage, index) => (
            <div
              key={stage.name}
              className="relative flex flex-col items-center"
            >
              {/* Funnel Stage */}
              <div
                className="relative"
                style={{
                  width: `${getStageWidth(index, funnelData.length)}%`,
                  minHeight: "60px",
                }}
              >
                <Tooltip
                  title={`${stage.count} leads (${stage.percentage.toFixed(
                    1
                  )}% of total)`}
                  placement="top"
                >
                  <div
                    className="relative overflow-hidden shadow-lg cursor-pointer"
                    style={{
                      background: `linear-gradient(135deg, ${stage.color}, ${stage.color}dd)`,
                      borderRadius: "30px",
                      border: "2px solid rgba(255,255,255,0.2)",
                      height: "60px",
                    }}
                  >
                    <div className="flex items-center justify-between h-full px-6 relative z-10">
                      <div className="flex items-center space-x-3">
                        <div className="text-xl text-white opacity-90">
                          {stage.icon}
                        </div>
                        <div>
                          <div className="font-bold text-white text-sm">
                            {stage.name}
                          </div>
                          <div className="text-xs text-white opacity-80">
                            {stage.count} leads
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-white">
                          {stage.count}
                        </div>
                        <div className="text-xs text-white opacity-80">
                          {stage.percentage.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </div>
                </Tooltip>
              </div>

              {/* Connecting Arrow/Line */}
              {index < funnelData.length - 1 && (
                <div className="flex flex-col items-center my-1">
                  {/* Arrow */}
                  <div className="w-0 h-0 border-l-4 border-r-4 border-t-6 border-transparent border-t-gray-400 dark:border-t-gray-500"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Overall Conversion Rate */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="text-center">
          <Text className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
            Overall Conversion Rate
          </Text>
          <div className="flex items-center justify-center space-x-4">
            <Progress
              type="circle"
              percent={leadStats?.conversionRate || 0}
              size={80}
              strokeColor="#52c41a"
            />
            <div>
              <div className="text-2xl font-bold text-green-600">
                {leadStats?.conversionRate || 0}%
              </div>
              <Text className="text-sm text-gray-500 dark:text-gray-400">
                Leads to Deals
              </Text>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default LeadConversionFunnel;
